import { useQueryClient } from '@tanstack/react-query'
import pluralize from 'pluralize'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { CacheKeys } from '../../../../lib/cache'
import { GENERIC_ERROR } from '../../../../lib/errors'
import {
  type CertificateHolderImportResult,
  CertificateHolderImportStatus,
  getCertificateHolderImportStatus,
  importCertificateHolders,
} from '../../../../models/coi'

const POLL_INTERVAL_MS = 3000
// Backstop for an import whose worker died without recording an outcome, so the
// spinner cannot run forever. Counted in polls rather than wall-clock time —
// same ~10 minutes, and it keeps Date.now() out of the hook body.
const MAX_POLLS = (10 * 60 * 1000) / POLL_INTERVAL_MS

type ApiError = { response?: { data?: { detail?: unknown } } } | undefined

function importErrorMessage(error: unknown): string {
  const detail = (error as ApiError)?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : GENERIC_ERROR
}

function importSummary(result: CertificateHolderImportResult): string {
  const parts: string[] = []
  if (result.created) parts.push(`${pluralize('certificate holder', result.created, true)} added`)
  if (result.updated) parts.push(`${result.updated} updated`)
  if (result.failed) parts.push(`${result.failed} could not be imported`)
  if (result.truncated) parts.push(`${result.truncated} beyond the import limit were skipped`)
  if (parts.length === 0) return 'Every certificate holder in the file was already up to date'
  return parts.join(', ')
}

/**
 * Uploads a certificate holder list, then polls the backend for how the
 * background import actually went — reporting the outcome rather than leaving a
 * spinner to time out, which is the silent failure this flow replaced.
 */
export function useCertificateHolderImport(orgId?: string) {
  const [isUploading, setIsUploading] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const queryClient = useQueryClient()
  // Refs, not state: the polling loop reads them without needing to re-render,
  // and a stale closure here would keep polling a finished import.
  const documentId = useRef<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollsLeft = useRef(0)

  function stopPolling() {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    documentId.current = null
    setIsImporting(false)
  }

  useEffect(() => () => stopPolling(), [])

  function invalidateHolders() {
    queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_CERTIFICATE_HOLDERS] })
  }

  async function poll() {
    const id = documentId.current
    if (!id) return

    let result: CertificateHolderImportResult
    try {
      result = await getCertificateHolderImportStatus(id, orgId)
    } catch {
      // A transient failure to read the status is not a failed import — keep
      // polling until the deadline rather than reporting a false error.
      schedulePoll()
      return
    }
    if (documentId.current !== id) return

    if (result.status === CertificateHolderImportStatus.ERROR) {
      toast.error(result.error || 'The certificate holder import failed')
      invalidateHolders()
      stopPolling()
      return
    }
    if (result.status === CertificateHolderImportStatus.DONE) {
      toast.success(importSummary(result))
      invalidateHolders()
      stopPolling()
      return
    }

    invalidateHolders()
    schedulePoll()
  }

  function schedulePoll() {
    if (pollsLeft.current <= 0) {
      toast.error('The certificate holder import is taking longer than expected')
      stopPolling()
      return
    }
    pollsLeft.current -= 1
    timer.current = setTimeout(poll, POLL_INTERVAL_MS)
  }

  async function importFile(file: File | File[] | null): Promise<boolean> {
    if (!file || Array.isArray(file)) return false

    setIsUploading(true)
    let response
    try {
      response = await importCertificateHolders(file, orgId)
    } catch (error) {
      toast.error(importErrorMessage(error))
      return false
    } finally {
      setIsUploading(false)
    }

    if (timer.current) clearTimeout(timer.current)
    documentId.current = response.documentId
    pollsLeft.current = MAX_POLLS
    setIsImporting(true)
    invalidateHolders()
    schedulePoll()
    return true
  }

  return { importFile, isUploading, isImporting }
}
